"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "./lib/utils";

const iqButtonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-[var(--iq-border-radius-md)] font-medium transition-all duration-100 ease-out cursor-pointer select-none",
  {
    variants: {
      variant: {
        fill: "",
        outline: "border bg-white",
        colorOnHover: "",
        text: "",
      },
      size: {
        sm: "text-iq-sm h-[30px] px-[10px]",
        md: "text-iq-md h-[40px] px-[10px]",
        lg: "text-iq-lg h-[44px] px-iq-md",
        xl: "text-iq-xl h-[52px] px-iq-xl",
      },
      color: {
        orange: "",
        blue: "",
        greyLight: "",
        greyLighter: "",
        greyMedium: "",
        greyDark: "",
        white: "",
      },
      rounded: {
        true: "!rounded-[var(--iq-border-radius-lg)]",
        false: "",
      },
    },
    compoundVariants: [
      // Fill variants
      { variant: "fill", color: "orange", className: "bg-iq-orange text-iq-grey-dark hover:-translate-y-0.5 hover:shadow-[var(--iq-box-shadow-sm)]" },
      { variant: "fill", color: "blue", className: "bg-iq-blue text-white hover:-translate-y-0.5 hover:shadow-[var(--iq-box-shadow-sm)]" },
      { variant: "fill", color: "greyLight", className: "bg-iq-grey-light text-iq-grey-dark hover:-translate-y-0.5 hover:shadow-[var(--iq-box-shadow-sm)]" },
      { variant: "fill", color: "greyLighter", className: "bg-iq-grey-lighter text-iq-grey-dark hover:-translate-y-0.5 hover:shadow-[var(--iq-box-shadow-sm)]" },
      { variant: "fill", color: "white", className: "bg-white text-iq-grey-dark hover:-translate-y-0.5 hover:shadow-[var(--iq-box-shadow-sm)]" },
      // Outline variants
      { variant: "outline", color: "orange", className: "border-iq-orange text-iq-orange hover:-translate-y-0.5 hover:shadow-[var(--iq-box-shadow-sm)]" },
      { variant: "outline", color: "blue", className: "border-iq-blue text-iq-blue hover:-translate-y-0.5 hover:shadow-[var(--iq-box-shadow-sm)]" },
      { variant: "outline", color: "greyMedium", className: "border-iq-grey-medium text-iq-grey-dark hover:-translate-y-0.5 hover:shadow-[var(--iq-box-shadow-sm)]" },
      { variant: "outline", color: "greyDark", className: "border-iq-grey-dark text-iq-grey-dark hover:-translate-y-0.5 hover:shadow-[var(--iq-box-shadow-sm)]" },
      // ColorOnHover variants
      { variant: "colorOnHover", color: "greyLighter", className: "hover:bg-iq-grey-lighter" },
      { variant: "colorOnHover", color: "orange", className: "hover:bg-iq-orange hover:text-iq-grey-dark" },
      // Text variants
      { variant: "text", color: "orange", className: "text-iq-orange hover:underline" },
      { variant: "text", color: "greyDark", className: "text-iq-grey-dark hover:underline" },
    ],
    defaultVariants: {
      variant: "fill",
      size: "md",
      color: "orange",
      rounded: false,
    },
  }
);

export interface IQButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof iqButtonVariants> {
  href?: string;
  target?: React.HTMLAttributeAnchorTarget;
  icon?: React.ReactNode;
  iconPlacement?: "left" | "right";
}

const IQButton = React.forwardRef<HTMLButtonElement, IQButtonProps>(
  (
    {
      className,
      variant,
      size,
      color,
      rounded,
      href,
      target,
      icon,
      iconPlacement = "left",
      children,
      ...props
    },
    ref
  ) => {
    const inner = (
      <span
        className={cn(
          "w-full h-full flex items-center overflow-hidden",
          icon ? "justify-between" : "justify-center",
          icon && iconPlacement === "left" && "[&>svg]:mr-[5px] [&>svg]:-mt-0.5",
          icon && iconPlacement === "right" && "[&>svg]:ml-[5px] [&>svg]:-mt-0.5"
        )}
      >
        {iconPlacement === "left" && icon}
        {children}
        {iconPlacement === "right" && icon}
      </span>
    );

    if (href) {
      return (
        <a
          href={href}
          target={target}
          rel={target === "_blank" ? "nofollow noreferrer" : undefined}
          className={cn(
            iqButtonVariants({ variant, size, color, rounded, className })
          )}
        >
          {inner}
        </a>
      );
    }

    return (
      <button
        className={cn(
          iqButtonVariants({ variant, size, color, rounded, className })
        )}
        ref={ref}
        {...props}
      >
        {inner}
      </button>
    );
  }
);
IQButton.displayName = "IQButton";

export { IQButton, iqButtonVariants };
